import { createContext, useCallback, useContext, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';

type HeaderOverlay = 'tools' | 'user' | 'ask' | 'suggestion' | 'actions' | 'search';
const HeaderOverlayContext = createContext<{
  active: HeaderOverlay | null;
  setActive: Dispatch<SetStateAction<HeaderOverlay | null>>;
} | null>(null);

export function HeaderOverlayProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<HeaderOverlay | null>(null);
  const value = useMemo(() => ({ active, setActive }), [active]);
  return <HeaderOverlayContext.Provider value={value}>{children}</HeaderOverlayContext.Provider>;
}

export function useHeaderOverlay(name: HeaderOverlay) {
  const context = useContext(HeaderOverlayContext);
  if (!context) throw new Error('Header overlays require HeaderOverlayProvider');
  const { active, setActive } = context;
  const setOpen = useCallback<Dispatch<SetStateAction<boolean>>>(next => {
    setActive(current => {
      const open = typeof next === 'function' ? next(current === name) : next;
      return open ? name : current === name ? null : current;
    });
  }, [name, setActive]);
  return [active === name, setOpen] as const;
}

export function useDismissHeaderOverlays() {
  const context = useContext(HeaderOverlayContext);
  if (!context) throw new Error('Header overlays require HeaderOverlayProvider');
  const { setActive } = context;
  return useCallback(() => setActive(null), [setActive]);
}
